// Kinio Node 的 UART1 接著哪台設備（SPEC #vms-web-s5-19-2 NODE-06、#node-s11-7-3「UART1 接了哪台設備」；VMS UX 評審第十一輪 R11-P1-2，第 103 列）。
// 資料是 VMS façade（GET /v1/homes/{home}/vms/devices）Kinio Node 那一列的 ttl_target {label, device_ref?, name?, presence?, rx_at?}：
//   帶 device_ref = 同家戶、呼叫者有 device.read 的設備；只有 label = 看不到（或不是 Kinio 設備，façade 不分、不洩漏 AID）；
//   鍵不在 = Node 沒回報 kinio_node.ttl_dut。
import type { TFunction } from 'i18next';
import { fullName, type Presence, type VmsDevice } from '../../lib/devices';

export interface TtlTarget {
  label: string;
  /** 對到的那台（同家戶、看得到才有） */
  deviceRef?: string;
  name?: string;
  presence?: Presence;
  /** 接著的設備最後有輸出的時間（RFC 3339） */
  rxAt?: string;
}

/** Node 韌體從這一版開始回報 ttl_dut（kinio-node 5151ec2，BL602 1.0.56）。 */
export const TTL_DUT_MIN_FW = '1.0.56';
/** rx_at 超過這麼久寫「很久沒有輸出」（NODE-06：24 小時，Claude 2026-10-15 定）。 */
export const TTL_QUIET_MS = 24 * 3600 * 1000;

const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);

/** façade ttl_target → TtlTarget；鍵不在 / 形狀不對（沒有 label）→ null（= 沒回報）。 */
export function parseTtlTarget(v: unknown): TtlTarget | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const label = str(o.label);
  if (!label) return null;
  const out: TtlTarget = { label };
  const ref = str(o.device_ref);
  if (ref) {
    out.deviceRef = ref;
    out.name = str(o.name);
    const st = (o.presence as { state?: unknown } | undefined)?.state;
    out.presence = st === 'online' ? 'online' : st === 'offline' ? 'offline' : 'unknown';
  }
  const rx = str(o.rx_at);
  if (rx && Number.isFinite(Date.parse(rx))) out.rxAt = rx;
  return out;
}

/** 版本 ≥ min（x.y.z 數字比較）；讀不懂（空、非數字）→ undefined（不判斷，NODE-06 ③）。 */
export function fwAtLeast(v: string | undefined, min: string): boolean | undefined {
  const parse = (s: string) => {
    const m = /^v?(\d+)\.(\d+)\.(\d+)/.exec(s.trim());
    return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
  };
  const a = v ? parse(v) : null;
  const b = parse(min);
  if (!a || !b) return undefined;
  for (let i = 0; i < 3; i++) if (a[i] !== b[i]) return a[i] > b[i];
  return true;
}

/**
 * 韌體低於 1.0.56 的 Node 要不要叫人升級（NODE-06 ③；第十二輪 B12-03）：看韌體分頁同一份 OTA 狀態——
 * 更新通道真的有 ≥ 1.0.56（update_available 而且 release.version ≥ 1.0.56）→ available（給〔看韌體〕）；
 * 沒有可用更新、可用更新還低於 1.0.56、或讀不到 OTA 狀態 → unreleased（不叫人升級）；還沒回來 → null（先不畫）。
 */
export function uartUpgrade(fw: { update_available?: boolean; release?: { version?: string } | null } | undefined, error: boolean): { kind: 'available'; version: string } | { kind: 'unreleased' } | null {
  if (!fw) return error ? { kind: 'unreleased' } : null;
  const v = fw.release?.version ?? '';
  if (fw.update_available && v && fwAtLeast(v, TTL_DUT_MIN_FW) === true) return { kind: 'available', version: v };
  return { kind: 'unreleased' };
}

export type RxState = 'none' | 'recent' | 'quiet' | 'node_offline';

/** 輸出時間怎麼寫（NODE-06 ④）：沒有 rx_at / 24 小時內 / 超過 24 小時；Node 離線時不判斷久不久。 */
export function rxState(rxAt: string | undefined, nodePresence: Presence, now = Date.now()): RxState {
  if (!rxAt) return 'none';
  if (nodePresence === 'offline') return 'node_offline';
  return now - Date.parse(rxAt) > TTL_QUIET_MS ? 'quiet' : 'recent';
}

/**
 * 對到的那台：在設備樹裡（同家戶）就用樹裡的名字與狀態（跟設備清單同一份、即時更新），不在才用 façade 帶的。
 * 沒有 device_ref → null（只寫 label、不給連結）。
 */
export function ttlLinked(node: Pick<VmsDevice, 'homeId'>, tt: TtlTarget, tree: VmsDevice[]): { homeId: string; aid: string; name: string; presence: Presence } | null {
  if (!tt.deviceRef) return null;
  const all = tree.flatMap((d) => [d, ...d.children]);
  const d = all.find((x) => x.homeId === node.homeId && x.aid === tt.deviceRef);
  if (d) return { homeId: d.homeId, aid: d.aid, name: fullName(d, tree), presence: d.presence };
  return { homeId: node.homeId, aid: tt.deviceRef, name: tt.name || tt.label, presence: tt.presence ?? 'unknown' };
}

/** 接著的那台怎麼稱呼（概覽、清單滑過、診斷區標題同一個函式）：對到的用樹裡 / façade 的名字，看不到的用 label；沒回報 / façade 還沒回來 → ''。 */
export function ttlName(node: VmsDevice, tree: VmsDevice[]): string {
  const tt = node.ttlTarget;
  if (!tt) return '';
  return ttlLinked(node, tt, tree)?.name ?? tt.label;
}

/** 一句話（設備清單滑過、Station 節點區小字）：「UART（TTL）接著的設備：PAC122 前門」；沒回報 / façade 還沒回來 → ''。 */
export function ttlSummary(t: TFunction, node: VmsDevice, tree: VmsDevice[]): string {
  const name = ttlName(node, tree);
  return name ? t('nuart.summary', { name }) : '';
}
